import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { SourceStatusEnum } from '@am3nd-org/pointcraft-types';

import { MOCK_ACTIVITIES } from '../mock';
import { storeEvents, STORE_EVENTS } from '../utils';
import type {
  Customer,
  Employee,
  Transaction,
  ActivityItem,
  ActivityType,
  RecurringPlan,
} from '../types';

/**
 * Helper to check if a status represents a successful/paid transaction
 */
const isCompletedStatus = (status: SourceStatusEnum): boolean =>
  status === SourceStatusEnum.PAID || status === SourceStatusEnum.OVERPAID;

/* =================== ACTIVITY STORE STATE =================== */
interface ActivityState {
  activities: ActivityItem[];
  isLoading: boolean;
  error: string | null;

  // OPERATIONS
  getActivities: () => ActivityItem[];
  getActivityById: (id: string) => ActivityItem | undefined;
  addActivity: (activity: Omit<ActivityItem, 'id' | 'timestamp'>) => ActivityItem;
  getActivitiesByType: (type: ActivityType) => ActivityItem[];
  getActivitiesByDateRange: (start: Date, end: Date) => ActivityItem[];
  getRecentActivities: (limit?: number) => ActivityItem[];

  // UTILITY
  refreshActivities: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

/* =================== ACTIVITY STORE =================== */
export const useActivityStore = create<ActivityState>()(
  devtools(
    (set, get) => {
      // SET UP EVENT LISTENERS FOR AUTOMATIC ACTIVITY CREATION
      setupActivityEventListeners(set, get);

      return {
        activities: [...MOCK_ACTIVITIES],
        isLoading: false,
        error: null,

        // OPERATIONS
        getActivities: () => get().activities,

        getActivityById: (id: string) => get().activities.find((a) => a.id === id),

        addActivity: (activity) => {
          const newActivity: ActivityItem = {
            ...activity,
            id: `act_${crypto.randomUUID()}`,
            timestamp: new Date(),
          };
          set((state) => ({
            activities: [newActivity, ...state.activities],
          }));
          return newActivity;
        },

        getActivitiesByType: (type) => get().activities.filter((a) => a.type === type),

        getActivitiesByDateRange: (start, end) =>
          get().activities.filter((a) => a.timestamp >= start && a.timestamp <= end),

        getRecentActivities: (limit = 10) =>
          [...get().activities]
            .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
            .slice(0, limit),

        // UTILITY
        refreshActivities: () => {
          set({ activities: [...MOCK_ACTIVITIES] });
        },

        setLoading: (loading) => set({ isLoading: loading }),

        setError: (error) => set({ error }),
      };
    },
    { name: 'activity-store' }
  )
);

/* =================== EVENT LISTENERS SETUP =================== */

function setupActivityEventListeners(
  set: (partial: Partial<ActivityState>) => void,
  get: () => ActivityState
) {
  // TRANSACTION EVENTS
  storeEvents.on(STORE_EVENTS.TRANSACTION_CREATED, (data) => {
    const transaction = data as Transaction;
    useActivityStore.getState().addActivity({
      type: 'transaction',
      title: 'New Transaction Created',
      description: `Transaction of $${(transaction.amount / 100).toFixed(2)} ${transaction.status}`,
      icon: 'solar:dollar-bold',
      color: isCompletedStatus(transaction.status) ? 'success' : 'warning',
    });
  });

  storeEvents.on(STORE_EVENTS.TRANSACTION_UPDATED, (data) => {
    const transaction = data as Transaction;
    useActivityStore.getState().addActivity({
      type: 'transaction',
      title: 'Transaction Updated',
      description: `Transaction updated to ${transaction.status}`,
      icon: 'solar:pen-bold',
      color: 'info',
    });
  });

  storeEvents.on(STORE_EVENTS.TRANSACTION_DELETED, (data) => {
    const transaction = data as Transaction;
    useActivityStore.getState().addActivity({
      type: 'transaction',
      title: 'Transaction Deleted',
      description: `Transaction of $${(transaction.amount / 100).toFixed(2)} was deleted`,
      icon: 'solar:trash-bin-trash-bold',
      color: 'error',
    });
  });

  // CUSTOMER EVENTS
  storeEvents.on(STORE_EVENTS.CUSTOMER_CREATED, (data) => {
    const customer = data as Customer;
    useActivityStore.getState().addActivity({
      type: 'customer',
      title: 'New Customer Added',
      description: `${customer.firstName} ${customer.lastName} joined`,
      icon: 'solar:user-plus-bold',
      color: 'success',
    });
  });

  storeEvents.on(STORE_EVENTS.CUSTOMER_UPDATED, (data) => {
    const customer = data as Customer;
    useActivityStore.getState().addActivity({
      type: 'customer',
      title: 'Customer Updated',
      description: `${customer.firstName} ${customer.lastName} information updated`,
      icon: 'solar:pen-bold',
      color: 'info',
    });
  });

  storeEvents.on(STORE_EVENTS.CUSTOMER_DELETED, (data) => {
    const customer = data as Customer;
    useActivityStore.getState().addActivity({
      type: 'customer',
      title: 'Customer Removed',
      description: `${customer.firstName} ${customer.lastName} was removed`,
      icon: 'solar:user-minus-bold',
      color: 'error',
    });
  });

  // EMPLOYEE EVENTS
  storeEvents.on(STORE_EVENTS.EMPLOYEE_CREATED, (data) => {
    const employee = data as Employee;
    useActivityStore.getState().addActivity({
      type: 'employee',
      title: 'New Employee Added',
      description: `${employee.name} joined as ${employee.role}`,
      icon: 'solar:user-plus-bold',
      color: 'success',
    });
  });

  storeEvents.on(STORE_EVENTS.EMPLOYEE_UPDATED, (data) => {
    const employee = data as Employee;
    useActivityStore.getState().addActivity({
      type: 'employee',
      title: 'Employee Updated',
      description: `${employee.name} information updated`,
      icon: 'solar:pen-bold',
      color: 'info',
    });
  });

  storeEvents.on(STORE_EVENTS.EMPLOYEE_DELETED, (data) => {
    const employee = data as Employee;
    useActivityStore.getState().addActivity({
      type: 'employee',
      title: 'Employee Removed',
      description: `${employee.name} was removed`,
      icon: 'solar:user-minus-bold',
      color: 'error',
    });
  });

  // PLAN EVENTS
  storeEvents.on(STORE_EVENTS.PLAN_CREATED, (data) => {
    const plan = data as RecurringPlan;
    useActivityStore.getState().addActivity({
      type: 'plan',
      title: 'New Plan Created',
      description: `${plan.name} plan created`,
      icon: 'solar:document-add-bold',
      color: 'success',
    });
  });

  storeEvents.on(STORE_EVENTS.PLAN_UPDATED, (data) => {
    const plan = data as RecurringPlan;
    useActivityStore.getState().addActivity({
      type: 'plan',
      title: 'Plan Updated',
      description: `${plan.name} plan updated`,
      icon: 'solar:pen-bold',
      color: 'info',
    });
  });

  storeEvents.on(STORE_EVENTS.PLAN_DELETED, (data) => {
    const plan = data as RecurringPlan;
    useActivityStore.getState().addActivity({
      type: 'plan',
      title: 'Plan Deleted',
      description: `${plan.name} plan was deleted`,
      icon: 'solar:trash-bin-trash-bold',
      color: 'error',
    });
  });

  storeEvents.on(STORE_EVENTS.PLAN_ACTIVATED, (data) => {
    const plan = data as RecurringPlan;
    useActivityStore.getState().addActivity({
      type: 'plan',
      title: 'Plan Activated',
      description: `${plan.name} plan is now active`,
      icon: 'solar:check-circle-bold',
      color: 'success',
    });
  });

  storeEvents.on(STORE_EVENTS.PLAN_CANCELLED, (data) => {
    const plan = data as RecurringPlan;
    useActivityStore.getState().addActivity({
      type: 'plan',
      title: 'Plan Cancelled',
      description: `${plan.name} plan was cancelled`,
      icon: 'solar:close-circle-bold',
      color: 'warning',
    });
  });
}
